import { action, computed, reaction, observable, makeObservable } from 'mobx';
import {clientId, Disposers} from '../../../../../../lib/helpers';
import {X9ContentStateStore} from '../../../common/X9ContentStateStore';
import {SyncedAudioModel} from '../../models';
import {AudioElementStore} from '../common/AudioElementStore';
import {AudioProgressStore} from '../common/AudioProgressStore';
import {PlayTimeRange, StudentPlayerState, SyncedAudioState} from '../models';

export class StudentSyncedAudioStore {
  readonly audio = new AudioElementStore();
  readonly progress = new AudioProgressStore();

  private readonly disposers = new Disposers();

  @observable.shallow
  readonly trackedStudents = new Map<number, AudioProgressStore>();

  private playCount = Number.MIN_SAFE_INTEGER;

  constructor(readonly id: number,
              readonly model: SyncedAudioModel,
              readonly stateStore: X9ContentStateStore) {
    makeObservable(this);
    this.disposers.add(this.audio);
    this.disposers.add(this.progress);
    this.setup();
  }

  private setup() {
    this.progress.syncAudioDurationFrom(this.audio);
    this.progress.syncBufferedFrom(this.audio);
    this.progress.syncCurrentTimeFrom(this.audio);
    // this.disposers.add(reaction(
    //   () => this.syncedAudioStudents,
    //   this.addCurrentClientId,
    //   {fireImmediately: true}));
    this.disposers.add(reaction(
        () => this.syncedAudioStudents,
        this.addCurrentClientId));
    this.disposers.add(reaction(
      () => [this.audio.currentTime, this.audio.buffered],
      this.notifyStudentState,
      {fireImmediately: true}));
    this.disposers.add(reaction(
      () => this.playCommand,
      this.executePlayCommand,
      {fireImmediately: true}
    ));
    this.disposers.add(reaction(
      () => this.audio.currentTime,
      this.stopPlayRange,
      {fireImmediately: true}
    ));
  }

  @computed
  private get syncedAudioStudents(): SyncedAudioState {
    const studentIds: number[] = [0];
    const altSyncedAudioStudents: SyncedAudioState = { studentIds };
    return this.stateStore.getContentState<SyncedAudioState>(this.id) ? this.stateStore.getContentState<SyncedAudioState>(this.id)! : altSyncedAudioStudents;
  }

  @action
  private addCurrentClientId = () => {
    const students = this.syncedAudioStudents;
    const studentIds = students.studentIds.slice();
    studentIds.push(clientId);
    this.stateStore.setContentState<SyncedAudioState>(this.id, {studentIds});
  };

  @computed
  private get studentState(): StudentPlayerState {
    return {
      currentTime: this.audio.currentTime,
      buffered: this.audio.buffered
    };
  }

  @action
  private notifyStudentState = () => {
    this.stateStore.setScopedContentState<StudentPlayerState>(this.id, clientId, this.studentState);
  };

  @computed
  private get playCommand(): PlayTimeRange | null {
    return this.stateStore.getScopedContentState(this.id, 'command');
  }

  @action
  executePlayCommand = () => {
    const cmd = this.playCommand;
    if (cmd === null || cmd.start === null) {
      this.audio.pause();
      return;
    }
    if (cmd.commandId === this.playCount)
      return;
    this.playCount = cmd.commandId;
    this.audio.setCurrentTime(cmd.start);
    this.audio.play();
  };

  @action
  stopPlayRange = () => {
    const cmd = this.playCommand;
    if (cmd === null || cmd.start === null) {
      // this.audio.pause();
      return;
    }
    if (cmd.stop === null)
      return;
    if (cmd.stop < this.audio.currentTime)
      this.audio.pause();
  };

  dispose() {
    this.disposers.dispose();
  }
}
